import React, { FormEvent, useState } from 'react';
import { TextInput, Button } from '@razorpay/blade/components';
import {
  FormWrapper,
  StepActionsWrapper,
  StepContentWrapper,
} from 'components/WizardViews/components';
import { useForm } from 'forms';
import { StatutoryContributionsFormData } from './types';
import { useUserId } from '../BankAccountDetails/hooks/useUserId';
import { useToast } from '@razorpay/blade/components';
import { validateContractorStatutoryContributions } from './utils';
import { EmployeeStatutorySettingsUpdateRequest } from 'api/employeeOnboarding';
import { useMutation } from 'react-query';
import employeeOnboarding from 'api/employeeOnboarding';
import { AppError, getAppErrorDetails } from 'utils/AppError';
import { parseError } from 'utils/Api';
import DuplicateChecksModal, { DuplicateChecksModalProps } from './components/DuplicateChecksModal';
import { SectionWrapper } from './components/SectionWrapper';
import useReduxSelector from 'utils/useReduxSelector';
import loggedInUserSelectors from 'reducers/loggedInUser/selectors';
import { isContractorsPanValidationEnabled } from 'utils/getContractorText';

export const ContractorStatutoryContributionsForm = ({
  formInitialData,
  onClose,
}: {
  formInitialData: StatutoryContributionsFormData;
  onClose: () => void;
}) => {
  const toast = useToast();
  const userId = useUserId();
  const featureFlags = useReduxSelector(loggedInUserSelectors.features);
  const isRxCurrentAccountMerchant = !!useReduxSelector(loggedInUserSelectors.currentOrganization)
    ?.isRxCurrentAccountMerchant;

  const isContractorPanValidationEnabled = isContractorsPanValidationEnabled({
    isRxCurrentAccountMerchant,
    featureFlags,
  });

  const [isDuplicateChecksModalOpen, setIsDuplicateChecksModalOpen] = useState(false);

  const { validate, formData, register, clearFieldError } =
    useForm<StatutoryContributionsFormData>(formInitialData);

  const {
    mutate: updateStatutorySettings,
    isLoading: isUpdatingStatutorySettings,
    error,
  } = useMutation({
    mutationFn: (settings: EmployeeStatutorySettingsUpdateRequest) => {
      if (!userId) {
        throw new Error('User ID is required');
      }
      return employeeOnboarding.updateEmployeeStatutorySettings(userId, settings);
    },
    onSuccess: () => {
      toast.show({
        content: 'Statutory Contributions updated successfully',
        color: 'positive',
      });
      onClose();
    },
    onError: (e: typeof AppError) => {
      const isDuplicateCheckError = getAppErrorDetails(e).code === 'USER_DETAILS_ALREADY_EXISTS';
      if (isDuplicateCheckError) {
        setIsDuplicateChecksModalOpen(true);
      }
      toast.show({
        content: parseError(e),
        color: 'negative',
      });
    },
  });

  const clearErrorOnFocus = (fieldName: keyof StatutoryContributionsFormData) =>
    clearFieldError(fieldName);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    // Perform validation logic here
    const validationResult = await validate(
      validateContractorStatutoryContributions({
        isContractorPanValidationEnabled,
      }),
    );

    if (validationResult) {
      updateStatutorySettings({
        tds_settings: {
          aadhaar_number: formData.aadharNumber ?? '',
          pan: formData.pan ?? '',
        },
        pf_settings: {
          is_pf_enabled: false,
          pf_status: '',
          pf_uan: '',
          is_aadhaar_linked_to_uan: false,
          include_employer_contribution_in_ctc: false,
          contribute_to_eps: false,
        },
        esic_settings: {
          is_esic_enabled: false,
          esic_status: '',
          esic_ip_number: '',
        },
        pt_settings: {
          is_pt_enabled: false,
          pt_state: '',
        },
        lwf_settings: {
          is_lwf_enabled: false,
          lwf_location: '',
        },
        nps_settings: {
          is_nps_enabled: false,
          nps_pran_number: '',
        },
      });
    }
  };

  const handleCancel = () => {
    onClose();
  };

  const duplicateDetails = getAppErrorDetails(error)?.otherDetails?.jsonResponse
    ?.extraDetails as DuplicateChecksModalProps['details'];

  return (
    <>
      <FormWrapper onSubmit={handleSubmit}>
        <StepContentWrapper maxWidth="700px">
          {/* Tax Deducted at Source Section */}
          <SectionWrapper
            title="Tax Deducted at Source"
            description="Verify identity using Aadhaar and PAN to prevent fraud and ensure compliance."
            showDivider={false}>
            <TextInput
              label="PAN"
              placeholder="ABCDE1234F"
              necessityIndicator="required"
              {...register('pan')}
              onFocus={() => clearErrorOnFocus('pan')}
              isRequired
            />

            <TextInput
              label="Aadhaar Number"
              placeholder="Enter Aadhaar Number"
              {...register('aadharNumber')}
              onFocus={() => clearErrorOnFocus('aadharNumber')}
              helpText="Aadhar number is mandatory for Personal PAN and must be linked to PAN."
              {...(isContractorPanValidationEnabled ? {
                isRequired: true,
                necessityIndicator: 'required',
              } : {})}
            />
          </SectionWrapper>
        </StepContentWrapper>

        <StepActionsWrapper>
          <Button
            variant="tertiary"
            isDisabled={isUpdatingStatutorySettings}
            onClick={handleCancel}
            type="button">
            Cancel
          </Button>
          <Button type="submit" isLoading={isUpdatingStatutorySettings}>
            Save
          </Button>
        </StepActionsWrapper>
      </FormWrapper>

      <DuplicateChecksModal
        isOpen={isDuplicateChecksModalOpen}
        onDismiss={() => setIsDuplicateChecksModalOpen(false)}
        details={duplicateDetails}
      />
    </>
  );
};
